// /translate/naati/online/$country — NAATI Online country page (TH)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { NAATI_COUNTRIES, getNaatiCountry } from "@/data/naati-countries";
import { naatiCountrySectionsTh } from "@/data/naati-online-content";
import { PoliceSections } from "@/components/PoliceSections";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import NaatiTripleKeyLinks from "@/components/NaatiTripleKeyLinks";
import NaatiAuthorByline from "@/components/NaatiAuthorByline";
import NaatiDoctypeMatrix from "@/components/NaatiDoctypeMatrix";
import { naatiHowToSchema, naatiSpeakableSchema, naatiOfferCatalog } from "@/lib/naati-schema";
import { NAATI_ACT_SLUGS } from "@/routes/en.translate.naati.$destination.$act";
import { getNotaryAct } from "@/data/notary-acts";
import { Plane, MessageCircle, Phone, BadgeCheck, Stamp, ArrowRight } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/translate/naati/online/$country")({
  loader: ({ params }) => {
    if (!getNaatiCountry(params.country)) throw notFound();
    return { slug: params.country };
  },
  head: ({ params }) => {
    const c = getNaatiCountry(params.country);
    if (!c) return { meta: [{ title: ctaTitle("Not found", "/translate/naati/online/$country", "th")}] };
    const url = `${SITE}/translate/naati/online/${c.slug}`;
    const title = `NAATI Online ส่ง${c.nameTh} (${c.nameEn}) — แปลรับรอง DHL Express ${c.shippingDays} | NYC`;
    const desc = `บริการแปลรับรอง NAATI ส่ง${c.nameTh} ใช้กับ ${c.visaTypes.slice(0, 3).join(", ")} /หน้า + DHL Express — ส่งภาพเอกสารให้เราตรวจก่อน ฟรี`;
    return {
      meta: [
        { title: ctaTitle(title, "/translate/naati/online/$country", "th") }, { name: "description", content: ctaDesc(desc, "/translate/naati/online/$country", "th")},
        { property: "og:title", content: ctaTitle(title, "/translate/naati/online/$country", "th")}, { property: "og:description", content: ctaDesc(desc, "/translate/naati/online/$country", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/translate/naati/online/${c.slug}`, `/en/translate/naati/online/${c.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          naatiSpeakableSchema(url),
          naatiOfferCatalog({ url, inLanguage: "th" }),
          naatiHowToSchema({
            url,
            name: `วิธีสั่งแปล NAATI ส่ง${c.nameTh}`,
            inLanguage: "th",
            estimatedCostThb: 800 + c.shippingFeeThb,
            steps: [
              { name: "ส่งไฟล์", text: `ส่งสแกนเอกสาร (${c.popularDocs.slice(0,2).join(", ")}) ทาง LINE @nycli หรืออีเมล` },
              { name: "ยืนยันราคา", text: `รับใบเสนอราคา เริ่ม ฿800/หน้า + DHL ฿${c.shippingFeeThb.toLocaleString()} ไป${c.capitalTh}` },
              { name: "แปลและรับรอง", text: `นักแปล NAATI Professional แปล + ประทับตรา NAATI + CPN ภายใน 2-3 วันทำการ` },
              { name: "จัดส่ง DHL", text: `DHL Express ส่งถึง${c.capitalTh}ภายใน ${c.shippingDays} พร้อม Tracking Number` },
            ],
          }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `NAATI Translation to ${c.nameEn}`,
              serviceType: "NAATI Certified Translation",
              areaServed: { "@type": "Country", name: c.nameEn },
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Language Institute", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: 800, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
        ]),
      ],
    };
  },
  component: NaatiOnlineCountryThPage,
});

function NaatiOnlineCountryThPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const c = getNaatiCountry(slug)!;
  const sections = naatiCountrySectionsTh(c);
  const related = NAATI_COUNTRIES.filter((x) => x.region === c.region && x.slug !== c.slug).slice(0, 6);
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/translate/naati/online" className="hover:text-gold">NAATI Online</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{c.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Plane className="h-3.5 w-3.5" /> NAATI ส่ง {c.nameTh}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            แปลรับรอง NAATI ส่ง{c.nameTh} ({c.nameEn})
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            DHL Express ส่งถึง {c.capitalTh} ภายใน {c.shippingDays} · ค่าจัดส่งเริ่ม ฿{c.shippingFeeThb.toLocaleString()} · นักแปล NAATI Professional · เริ่ม ฿1,300/หน้า
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote data-speakable className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          แปลรับรอง NAATI ส่ง{c.nameTh} โดยนักแปล NAATI Professional · DHL Express ถึง {c.capitalTh} ใน {c.shippingDays} · เริ่ม ฿1,300/หน้า · {c.notes}
        </blockquote>

        <NaatiAuthorByline lang="th" />

        <div className="mt-10"><PoliceSections sections={sections} /></div>

        <section className="mt-12"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `NAATI ใช้ใน${c.nameTh}ได้ไหม?`, a: `${c.notes} หน่วยงานที่ยอมรับได้แก่ ${c.authorities.join(", ")}` },
                { q: `วีซ่า${c.nameTh}ประเภทไหนต้องใช้ NAATI?`, a: c.visaTypes.join(", ") },
                { q: `ส่ง DHL ไป${c.nameTh}กี่วัน?`, a: `${c.shippingDays} ค่าจัดส่งเริ่ม ฿${c.shippingFeeThb.toLocaleString()}` },
                { q: `เอกสารยอดนิยมที่แปลส่ง${c.nameTh}คืออะไร?`, a: c.popularDocs.join(" · ") },
                { q: `ราคาแปล NAATI เท่าไหร่?`, a: `เริ่ม ฿1,300/หน้าสำหรับเอกสารส่วนตัว · ฿1,200-1,500/หน้าสำหรับเอกสารการศึกษา · รวมตรา NAATI แล้ว` },
              ]}
            />
          </div>
        </section>

        {related.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ประเทศใกล้เคียงใน {c.region}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to="/translate/naati/online/$country" params={{ country: r.slug }} className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-gold/50">
                  <div className="font-display text-sm font-extrabold text-navy-deep">{r.nameTh}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">{r.shippingDays} · ฿{r.shippingFeeThb.toLocaleString()}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Phase P-LINK-NAATI-ACT-DEST-REVERSE — deep links into act × destination matrix */}
        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Stamp className="mr-2 inline-block h-6 w-6 text-amber-600" />
            แพ็คเกจ Notary + NAATI ส่ง{c.nameTh}
          </h2>
          <p className="mt-2 text-[14px] text-slate-700">
            สำหรับวีซ่าและการย้ายถิ่นที่ต้องการ <strong>ทั้งรับรองทนาย Notary และแปล NAATI</strong> รวมในชุดเดียว — เลือกประเภท notary ที่ตรงกับความต้องการของ {c.authorities[0]}
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {NAATI_ACT_SLUGS.map((slug) => {
              const a = getNotaryAct(slug);
              if (!a) return null;
              return (
                <Link
                  key={slug}
                  to="/translate/naati/$destination/$act"
                  params={{ destination: c.slug, act: slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                  data-track-zone="naati-country-act-forward-th"
                >
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-700">{a.nameTh}</div>
                  <div className="text-[11px] text-slate-500">{a.nameEn}</div>
                  <div className="mt-1 text-[11px] text-slate-500">รวมเริ่ม ฿{(a.priceFromTh + 800).toLocaleString()}</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
                    ดูแพ็คเกจ <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        <NaatiDoctypeMatrix lang="th" />

        <NaatiTripleKeyLinks mode="th" countryNameTh={c.nameTh} countryNameEn={c.nameEn} />
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
